import { partition, startCase } from 'lodash';
import { ISODateString } from 'types/shared/ISODateString';
import { IDocumentCategory } from '../IDocumentCategoryType';

export interface IStandardDocumentCategoryOption {
  label: string;
  value: string;
  versioned: boolean;
}

export interface IStandardDocumentCategoryResponse {
  name: string;
  isRepeatable: boolean;
  isUploadable: boolean;
  isObsolete: boolean;
}

export interface IStandardDocumentCategoryData {
  label: string;
  value: string;
  isRepeatable: boolean;
  isUploadable: boolean;
  isObsolete: boolean;
}

const categoryLabelMap = (name: string): string => {
  switch (name) {
    case 'AffidavitOfPropertyValue':
      return 'Affidavit of Property Value (APV';
    case 'AgentSignedT00':
      return 'Agent Signed T00';
    case 'AkaAffidavit':
      return 'AKA Affidavit';
    case 'BorrowersAuthorization':
      return "Borrower's Authorization";
    case 'BulkIdReports':
      return 'Bulk ID Reports';
    case 'CcrChecklist':
      return 'CCR Checklist';
    case 'EOInsurance':
      return 'E&O Insurance';
    case 'LongFormLendersPolicy':
      return "Long Form Lender's Policy";
    case 'FedEx':
      return 'FedEx';
    case 'MasterSettlementStatementExcel':
      return 'Master Settlement Statement - Excel';
    case 'OwnersPolicy':
      return "Owner's Policy";
    case 'PriorOwnersPolicy':
      return "Prior Owner's Policy";
    case 'ShortFormLendersPolicy':
      return "Short Form Lender's Policy";
    case 'T00':
      return 'T00';
    case 'T46':
      return 'T46';
    case 'T47':
      return 'T47';
    case 'UnexecutedSellerClosingDocumentsNotary':
      return 'Unexecuted Seller Closing Documents - Notary';
    case 'W9':
      return 'W9';
    case 'DivorceDocument':
      return 'Divorce Document(s)';
    case 'LLCDocument':
      return 'LLC Document(s)';
    case 'PreCloserChecklist':
      return 'Pre-Closer Checklist';
    case 'TaxPayoffBulkSpreadsheet':
      return 'Tax:Payoff Bulk Spreadsheet';
    case 'HOACCRsBulkSpreadsheet':
      return 'HOA:CCRs Bulk Spreadsheet';
    default:
      return startCase(name);
  }
};

export const convertStandardDocumentCategoryResponseToStandardDocumentCategoryData = (
  data: IStandardDocumentCategoryResponse[]
): IStandardDocumentCategoryData[] => {
  // Sort data to maintain order of categories in dropdown
  const sortedData = data.sort((a, b) => (a.isRepeatable < b.isRepeatable ? -1 : 1));
  return sortedData.map(({ name, isRepeatable, isUploadable, isObsolete }) => ({
    value: name,
    label: categoryLabelMap(name),
    isRepeatable,
    isUploadable,
    isObsolete,
  }));
};

export interface IOriginalFileNameMetadata {
  original_file_name?: string;
}

export interface IGoodThroughDateMetadata {
  good_through_date?: string;
}

export type IMetadata = IOriginalFileNameMetadata & IGoodThroughDateMetadata; // TODO: Add more

export type TStandardDocumentPublisherType =
  | 'Admin'
  | 'LenderUser'
  | 'LenderCompany'
  | 'ClientUser'
  | 'ClientCompany'
  | 'VendorUser'
  | 'ADMIN'
  | 'LENDER_USER'
  | 'LENDER_COMPANY'
  | 'CLIENT_USER'
  | 'CLIENT_COMPANY'
  | 'VENDOR_USER'
  | 'BorrowerUser'
  | 'SellerUser'
  | 'BORROWER_USER'
  | 'SELLER_USER';

export const prettyStandardDocumentPublisherType: Record<TStandardDocumentPublisherType, string> = {
  VendorUser: 'Vendor',
  Admin: 'Spruce',
  LenderUser: 'Lender',
  LenderCompany: 'Lender API',
  ClientUser: 'Client',
  ClientCompany: 'Client API',
  VENDOR_USER: 'Vendor',
  ADMIN: 'Spruce',
  LENDER_USER: 'Lender API',
  LENDER_COMPANY: 'Lender API',
  CLIENT_USER: 'Client API',
  CLIENT_COMPANY: 'Client API',
  SellerUser: 'Seller',
  BorrowerUser: 'Borrower',
  SELLER_USER: 'Seller',
  BORROWER_USER: 'Borrower',
};

export const getPrettyPublisherType = (
  type: TStandardDocumentPublisherType,
  isZillowClosingEnabled: boolean,
): string => {
  const baseValue = prettyStandardDocumentPublisherType[type];
  if (isZillowClosingEnabled && baseValue === 'Spruce') {
    return 'Zillow Closing';
  }
  return baseValue;
};

export interface IStandardDocument {
  id: string;
  order_id: string;
  category: string;
  file_file_name: string;
  file_content_type: 'application/pdf' | 'image/jpeg' | 'image/png';
  file_size: number;
  file_updated_at: Date; // ISO Date
  uploader_id: string;
  uploader_type: TStandardDocumentPublisherType;
  client_publisher_id?: string;
  client_published_at?: string;
  client_publisher_type?: TStandardDocumentPublisherType;
  vendor_publisher_id?: string;
  vendorPublisherType?: TStandardDocumentPublisherType;
  vendor_published_at?: string;
  required_at_signing: boolean;
  funding: boolean;
  metadata?: {
    original_file_name?: string;
    good_through_date?: ISODateString; // ISO Date
  };
  created_at: Date; // ISO Date
  updated_at: Date; // ISO Date
}

export interface IStandardDocumentVersionData {
  id: string;
  versionId: string;
  standardDocumentId: string;
  orderId: string;
  category: string;
  createdAt: ISODateString; // ISO Date
  updatedAt: ISODateString; // ISO Date
  fileName: string;
  fileContentType: TFileContentType;
  uploaderId: string;
  uploaderType: TStandardDocumentPublisherType;
  requiredAtSigning: boolean;
  funding: boolean;
  versionNumber: number;
  fileSize: number;
  fileUpdatedAt: ISODateString; // ISO Date
  metadata?: IStandardDocumentMetaData;
  clientPublisherId?: string;
  clientPublisherType?: TStandardDocumentPublisherType;
  clientPublishedAt?: string;
  vendorPublisherId?: string;
  vendorPublisherType?: TStandardDocumentPublisherType;
  vendorPublishedAt?: string;
  isRepeatable: boolean;
}

export interface IUpdatedDocumentResponse {
  id: string;
  createdAt: ISODateString; // ISO Date
  updatedAt: ISODateString; // ISO Date
  category: string;
  order: {
    id: string;
    createdAt: ISODateString; // ISO Date
    updatedAt: ISODateString; // ISO Date
    fileNumber: string;
    orderPhase: string;
    bulkId?: string;
    closesOn: ISODateString; // ISO Date
    county: string;
    curativeGrade?: string;
    externalStatus: string;
    loanNumber: string;
    transactionCategory: string;
    clientCompany?: string;
    lenderCompany?: string;
    parties?: string;
    propertyAddress?: string;
  };
  versions: Array<IStandardDocumentVersionData>;
}

export interface IStandardDocumentResponse {
  id: string;
  order_id: string;
  category: string;
  file_file_name: string;
  file_content_type: 'application/pdf' | 'image/jpeg' | 'image/png';
  file_size: number;
  file_updated_at: ISODateString; // ISO Date
  uploader_id: string;
  uploader_type: TStandardDocumentPublisherType;
  client_publisher_id?: string;
  client_publisher_type?: TStandardDocumentPublisherType;
  vendor_publisher_id?: string;
  vendor_publisher_type?: TStandardDocumentPublisherType;
  vendor_published_at?: string;
  client_published_at?: string;
  required_at_signing: boolean;
  funding: boolean;
  metadata?: {
    good_through_date?: ISODateString; // ISO Date
  };
  created_at: ISODateString; // ISO Date
  updated_at: ISODateString; // ISO Date
}

export interface IStandardDocumentMetaData {
  original_file_name?: string;
  good_through_date?: ISODateString; // ISO Date
  merged_documents?: { category: string; file_name: string; id: string }[];
}

export interface IStandardDocumentData {
  id: string;
  orderId: string;
  category: string;
  fileName: string;
  fileContentType: 'application/pdf' | 'image/jpeg' | 'image/png';
  fileSize: number;
  fileUpdatedAt: string;
  uploaderId: string;
  uploaderType: TStandardDocumentPublisherType;
  clientPublisherId?: string;
  clientPublisherType?: TStandardDocumentPublisherType;
  vendorPublisherId?: string;
  vendorPublisherType?: TStandardDocumentPublisherType;
  vendorPublishedAt?: string;
  clientPublishedAt?: string;
  requiredAtSigning: boolean;
  funding: boolean;
  metadata?: IStandardDocumentMetaData;
  createdAt: ISODateString; // ISO Date
  updatedAt: ISODateString; // ISO Date
}

const getLatestVersion = (versions: IStandardDocumentVersionData[]): IStandardDocumentVersionData => {
  const sortedVersions = versions.sort((a, b) => {
    const aVersion = a.versionNumber;
    const bVersion = b.versionNumber;
    return aVersion - bVersion;
  });

  return sortedVersions[sortedVersions.length - 1];
};

// TODO: Get last item in array
export const convertFromUpdatedDocumentResponseToUpdatedLatestDocumentVersionData = (
  data: IUpdatedDocumentResponse,
  categories?: IDocumentCategory[]
): IStandardDocumentVersionData => {
  const latestVersion = getLatestVersion(data.versions);
  return addDocumentVersionValue(
    {
      id: data.id,
      versionId: latestVersion.id,
      standardDocumentId: latestVersion.standardDocumentId,
      orderId: data.order.id,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      category: data.category,
      fileName: latestVersion.fileName || '',
      fileContentType: latestVersion.fileContentType,
      uploaderId: latestVersion.uploaderId,
      uploaderType: latestVersion.uploaderType,
      requiredAtSigning: latestVersion.requiredAtSigning,
      funding: latestVersion.funding,
      versionNumber: latestVersion.versionNumber,
      fileSize: latestVersion.fileSize,
      fileUpdatedAt: latestVersion.fileUpdatedAt,
      metadata: latestVersion.metadata,
      clientPublisherId: latestVersion.clientPublisherId,
      clientPublisherType: latestVersion.clientPublisherType,
      clientPublishedAt: latestVersion.clientPublishedAt,
      vendorPublisherId: latestVersion.vendorPublisherId,
      vendorPublisherType: latestVersion.vendorPublisherType,
      vendorPublishedAt: latestVersion.vendorPublishedAt,
      isRepeatable: latestVersion.isRepeatable,
    },
    categories
  );
};

export const convertFromUpdatedDocumentHistoryToUpdatedVersionArray = (
  data: IUpdatedDocumentResponse,
  version: IStandardDocumentVersionData
): IStandardDocumentVersionData => {
  return addDocumentVersionValue({
    id: data.id, // Document ID
    standardDocumentId: version.standardDocumentId, // Legacy Id
    versionId: version.id, // Version ID
    orderId: data.order.id,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    category: data.category,
    fileName: version.fileName || '',
    fileContentType: version.fileContentType,
    uploaderId: version.uploaderId,
    uploaderType: version.uploaderType,
    requiredAtSigning: version.requiredAtSigning,
    funding: version.funding,
    versionNumber: version.versionNumber,
    fileSize: version.fileSize,
    fileUpdatedAt: version.fileUpdatedAt,
    metadata: version.metadata,
    clientPublisherId: version.clientPublisherId,
    clientPublisherType: version.clientPublisherType,
    clientPublishedAt: version.clientPublishedAt,
    vendorPublisherId: version.vendorPublisherId,
    vendorPublisherType: version.vendorPublisherType,
    vendorPublishedAt: version.vendorPublishedAt,
    isRepeatable: version.isRepeatable,
  });
};
const repeatable = (documentCategory: string, documentCategories?: IDocumentCategory[]): boolean => {
  const category = documentCategories && documentCategories.find((opt) => opt.name === documentCategory);
  return category ? category.isRepeatable : true;
};
const addDocumentVersionValue = (
  document: IStandardDocumentVersionData,
  documentCategories?: IDocumentCategory[]
): IStandardDocumentVersionData => ({
  ...document,
  isRepeatable: repeatable(document.category, documentCategories),
});

export const sortedStandardDocuments = (
  documents: IStandardDocumentVersionData[],
  documentCategories: IDocumentCategory[]
): IStandardDocumentVersionData[] =>
  partition(
    documents
      .map((doc) => addDocumentVersionValue(doc, documentCategories))
      .sort((presentationDocA, presentationDocB) => (presentationDocA.fileName > presentationDocB.fileName ? 1 : -1)),
    (document: IStandardDocumentVersionData) => !document.isRepeatable
  ).flat();

export const convertFromStandardDocumentResponseToStandardDocumentData = (
  data: IStandardDocumentResponse
): IStandardDocumentData => {
  let document = {
    id: data.id,
    orderId: data.order_id,
    category: data.category,
    fileName: data.file_file_name,
    fileContentType: data.file_content_type,
    fileSize: data.file_size,
    fileUpdatedAt: data.file_updated_at,
    updatedAt: data.updated_at,
    uploaderId: data.uploader_id,
    uploaderType: data.uploader_type,
    clientPublisherId: data.client_publisher_id,
    clientPublisherType: data.client_publisher_type,
    vendorPublisherId: data.vendor_publisher_id,
    vendorPublisherType: data.vendor_publisher_type,
    vendorPublishedAt: data.vendor_published_at,
    clientPublishedAt: data.client_published_at,
    requiredAtSigning: data.required_at_signing,
    funding: data.funding,
    metadata: data.metadata,
    createdAt: data.created_at,
  };
  return document;
};

export const convertFromStandardDocumentResponseArrayToStandardDocumentDataArray = (
  data: IStandardDocumentResponse[]
): IStandardDocumentData[] => data.map(convertFromStandardDocumentResponseToStandardDocumentData);

export const convertFromUpdatedDocumentResponseToUpdatedLatestDocumentVersionArray = (
  data: IUpdatedDocumentResponse[],
  categories?: IDocumentCategory[]
): IStandardDocumentVersionData[] =>
  data.map((doc) => convertFromUpdatedDocumentResponseToUpdatedLatestDocumentVersionData(doc, categories));

export const convertFromUpdatedDocumentHistoryResponseToUpdatedVersionArray = (
  data: IUpdatedDocumentResponse
): IStandardDocumentVersionData[] => {
  return data.versions.map((version) => convertFromUpdatedDocumentHistoryToUpdatedVersionArray(data, version));
};

export interface IDocumentsSharedApplicationState {
  documentCategories: IDocumentCategory[];
  standardDocuments: IStandardDocument[];
}

export type TFileContentType = 'application/pdf' | 'image/jpeg' | 'image/png';
